import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import Loader from '../shared/displayComponents/loader/Loader';
import url from '../constants/config';
import './CoachInsights.scss';

const NOTE_TYPE_LABELS = {
  call: { label: 'Telefon', icon: 'fa-phone', bg: '#dbeafe', color: '#1d4ed8' },
  email: { label: 'Email', icon: 'fa-envelope', bg: '#d1fae5', color: '#065f46' },
  meeting: { label: 'Spotkanie', icon: 'fa-handshake', bg: '#ede9fe', color: '#5b21b6' },
  other: { label: 'Inne', icon: 'fa-sticky-note', bg: '#f1f5f9', color: '#475569' },
};

const RANGE_OPTIONS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

const TYPE_LABELS = {
  view_player_profile: 'Viewed profile',
  add_favorite_player: 'Added favorite',
  remove_favorite_player: 'Removed favorite',
  contact_player_sent: 'Contacted player',
  search_executed: 'Search',
  compare_add_player: 'Compare add',
  compare_remove_player: 'Compare remove',
  save_search_created: 'Saved search',
  recommendations_shown: 'Recs shown',
  recommendation_clicked: 'Rec clicked',
};

const TYPE_COLORS = {
  view_player_profile: { bg: '#eff6ff', color: '#3b82f6' },
  add_favorite_player: { bg: '#fef3c7', color: '#d97706' },
  remove_favorite_player: { bg: '#fef3c7', color: '#92400e' },
  contact_player_sent: { bg: '#d1fae5', color: '#059669' },
  search_executed: { bg: '#f3e8ff', color: '#7c3aed' },
  compare_add_player: { bg: '#e0f2fe', color: '#0284c7' },
  compare_remove_player: { bg: '#e0f2fe', color: '#0369a1' },
  save_search_created: { bg: '#fce7f3', color: '#be185d' },
  recommendations_shown: { bg: '#ecfdf5', color: '#047857' },
  recommendation_clicked: { bg: '#ecfdf5', color: '#065f46' },
};

const CoachInsights = () => {
  const { coachEmail } = useParams();
  const decodedEmail = decodeURIComponent(coachEmail);
  const { userRole } = useSelector(({ authorization }) => authorization);
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('30d');

  /* CRM notes */
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('call');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeleting, setNoteDeleting] = useState(null);

  const token = localStorage.getItem('authorization');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${url}/admin/coaches/${encodeURIComponent(decodedEmail)}/insights?range=${range}`,
        { headers },
      );
      setData(res.data);
    } catch (err) {
      console.error('Coach insights fetch error', err);
      setError(err.response?.data?.message || 'Failed to load coach insights.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedEmail, range]);

  const fetchNotes = useCallback(() => {
    axios
      .get(`${url}/admin/coaches/${encodeURIComponent(decodedEmail)}/notes`, { headers })
      .then(res => setNotes(res.data.notes || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedEmail]);

  useEffect(() => {
    if (userRole !== 'admin') {
      history.push('/available-players');
      return;
    }
    fetchInsights();
    fetchNotes();
  }, [fetchInsights, fetchNotes, userRole, history]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      const res = await axios.post(
        `${url}/admin/coaches/${encodeURIComponent(decodedEmail)}/notes`,
        { text: noteText.trim(), type: noteType },
        { headers }
      );
      setNotes(prev => [res.data.note, ...prev]);
      setNoteText('');
    } catch { /* noop */ }
    finally { setNoteSaving(false); }
  };

  const handleDeleteNote = async (noteId) => {
    setNoteDeleting(noteId);
    try {
      await axios.delete(
        `${url}/admin/coaches/${encodeURIComponent(decodedEmail)}/notes/${noteId}`,
        { headers }
      );
      setNotes(prev => prev.filter(n => n._id !== noteId));
    } catch { /* noop */ }
    finally { setNoteDeleting(null); }
  };

  const handlePlayerClick = playerId => {
    if (playerId) history.push(`/available-players/${playerId}`);
  };

  const timeAgo = dateStr => {
    if (!dateStr) return 'Never';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return <div className="coach-insights"><Loader /></div>;
  }

  if (error) {
    return (
      <div className="coach-insights">
        <div className="coach-insights__error">
          <i className="fas fa-exclamation-triangle" />
          <p>{error}</p>
          <button onClick={fetchInsights} className="coach-insights__retry">Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { coach, lastActiveAt, activityCounts, uniqueCounts, topFilters, recentEvents, totalEventsAllTime } = data;
  const ac = activityCounts || {};
  const uc = uniqueCounts || {};

  return (
    <div className="coach-insights">
      {/* Back link */}
      <button
        type="button"
        className="coach-insights__back"
        onClick={() => history.goBack()}
      >
        <i className="fas fa-arrow-left" /> Back
      </button>

      {/* Header */}
      <div className="coach-insights__header">
        <div className="coach-insights__avatar">
          {(coach.firstName || '?')[0].toUpperCase()}
        </div>
        <div className="coach-insights__header-info">
          <h1 className="coach-insights__name">
            {coach.firstName} {coach.lastName}
          </h1>
          <div className="coach-insights__meta">
            <span><i className="fas fa-envelope" /> {coach.email}</span>
            {coach.schoolName && <span><i className="fas fa-university" /> {coach.schoolName}</span>}
            {coach.division && <span><i className="fas fa-trophy" /> {coach.division}</span>}
            {coach.jobTitle && <span><i className="fas fa-id-badge" /> {coach.jobTitle}</span>}
          </div>
          <div className="coach-insights__header-row">
            <span className={`coach-insights__status ${lastActiveAt ? 'coach-insights__status--active' : 'coach-insights__status--inactive'}`}>
              <i className="fas fa-circle" /> {lastActiveAt ? `Active ${timeAgo(lastActiveAt)}` : 'Never active'}
            </span>
            {totalEventsAllTime > 0 && (
              <span className="coach-insights__total-events">
                {totalEventsAllTime.toLocaleString()} events all-time
              </span>
            )}
            {coach.joinedAt && (
              <span className="coach-insights__joined">
                Joined {new Date(coach.joinedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Range selector */}
      <div className="coach-insights__range">
        <span className="coach-insights__range-label">Period:</span>
        <div className="coach-insights__range-btns">
          {RANGE_OPTIONS.map(r => (
            <button
              key={r.value}
              type="button"
              className={`coach-insights__range-btn ${range === r.value ? 'coach-insights__range-btn--active' : ''}`}
              onClick={() => setRange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="coach-insights__kpis">
        <div className="coach-insights__kpi">
          <div className="coach-insights__kpi-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
            <i className="fas fa-eye" />
          </div>
          <div className="coach-insights__kpi-body">
            <div className="coach-insights__kpi-value">{ac.views || 0}</div>
            <div className="coach-insights__kpi-label">Profile Views</div>
            <div className="coach-insights__kpi-sub">{uc.playersViewed || 0} unique players</div>
          </div>
        </div>

        <div className="coach-insights__kpi">
          <div className="coach-insights__kpi-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
            <i className="fas fa-bookmark" />
          </div>
          <div className="coach-insights__kpi-body">
            <div className="coach-insights__kpi-value">{ac.favoritesAdded || 0}</div>
            <div className="coach-insights__kpi-label">Favorites Added</div>
            <div className="coach-insights__kpi-sub">{uc.playersFavorited || 0} unique players</div>
          </div>
        </div>

        <div className="coach-insights__kpi">
          <div className="coach-insights__kpi-icon" style={{ background: '#d1fae5', color: '#059669' }}>
            <i className="fas fa-paper-plane" />
          </div>
          <div className="coach-insights__kpi-body">
            <div className="coach-insights__kpi-value">{ac.contacts || 0}</div>
            <div className="coach-insights__kpi-label">Contacts Sent</div>
            <div className="coach-insights__kpi-sub">{uc.playersContacted || 0} unique players</div>
          </div>
        </div>

        <div className="coach-insights__kpi">
          <div className="coach-insights__kpi-icon" style={{ background: '#f3e8ff', color: '#7c3aed' }}>
            <i className="fas fa-search" />
          </div>
          <div className="coach-insights__kpi-body">
            <div className="coach-insights__kpi-value">{ac.searches || 0}</div>
            <div className="coach-insights__kpi-label">Searches</div>
          </div>
        </div>

        <div className="coach-insights__kpi">
          <div className="coach-insights__kpi-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
            <i className="fas fa-columns" />
          </div>
          <div className="coach-insights__kpi-body">
            <div className="coach-insights__kpi-value">{ac.compares || 0}</div>
            <div className="coach-insights__kpi-label">Compares</div>
          </div>
        </div>
      </div>

      {/* Top Filters */}
      {topFilters && (topFilters.sports?.length > 0 || topFilters.years?.length > 0 || topFilters.positions?.length > 0) && (
        <div className="coach-insights__section">
          <h2 className="coach-insights__section-title">
            <i className="fas fa-filter" /> Search Preferences
          </h2>
          <div className="coach-insights__filters-grid">
            {topFilters.sports?.length > 0 && (
              <div className="coach-insights__filter-card">
                <h3>Top Sports</h3>
                <ol className="coach-insights__rank-list">
                  {topFilters.sports.map((s, i) => (
                    <li key={i}>
                      <span className="coach-insights__rank-name">{s.value}</span>
                      <span className="coach-insights__rank-count">{s.count}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {topFilters.years?.length > 0 && (
              <div className="coach-insights__filter-card">
                <h3>Top Grad Years</h3>
                <ol className="coach-insights__rank-list">
                  {topFilters.years.map((y, i) => (
                    <li key={i}>
                      <span className="coach-insights__rank-name">{y.value}</span>
                      <span className="coach-insights__rank-count">{y.count}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {topFilters.positions?.length > 0 && (
              <div className="coach-insights__filter-card">
                <h3>Top Positions</h3>
                <ol className="coach-insights__rank-list">
                  {topFilters.positions.map((p, i) => (
                    <li key={i}>
                      <span className="coach-insights__rank-name">{p.value}</span>
                      <span className="coach-insights__rank-count">{p.count}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CRM Notes */}
      <div className="coach-insights__section">
        <h2 className="coach-insights__section-title">
          <i className="fas fa-sticky-note" /> Notatki CRM
        </h2>

        {/* Add note form */}
        <form onSubmit={handleAddNote} style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 auto' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Typ</label>
            <select
              value={noteType}
              onChange={e => setNoteType(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff' }}
            >
              {Object.entries(NOTE_TYPE_LABELS).map(([val, meta]) => (
                <option key={val} value={val}>{meta.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Notatka</label>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              rows={2}
              placeholder="Np. 'Dzwoniłem, interesuje go volleyball, oddzwoni w marcu'"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="submit"
            disabled={noteSaving || !noteText.trim()}
            style={{ padding: '8px 18px', background: noteSaving || !noteText.trim() ? '#e2e8f0' : '#2e496b', color: noteSaving || !noteText.trim() ? '#94a3b8' : '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '14px', cursor: noteSaving || !noteText.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end', marginBottom: '1px' }}
          >
            {noteSaving ? 'Zapisywanie...' : '+ Dodaj'}
          </button>
        </form>

        {/* Notes list */}
        {notes.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '14px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
            Brak notatek CRM dla tego trenera.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {notes.map(note => {
              const meta = NOTE_TYPE_LABELS[note.type] || NOTE_TYPE_LABELS.other;
              return (
                <div key={note._id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ background: meta.bg, color: meta.color, borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <i className={`fas ${meta.icon}`} style={{ marginRight: '4px' }} />{meta.label}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#1e293b', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{note.text}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>
                      {note.authorEmail} · {new Date(note.createdAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteNote(note._id)}
                    disabled={noteDeleting === note._id}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', padding: '2px 4px', flexShrink: 0 }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; }}
                  >
                    <i className="fas fa-trash-alt" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Events */}
      {recentEvents && recentEvents.length > 0 && (
        <div className="coach-insights__section">
          <h2 className="coach-insights__section-title">
            <i className="fas fa-clock" /> Recent Activity (last 20)
          </h2>
          <div className="coach-insights__events-wrap">
            <table className="coach-insights__events-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Player</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map(ev => {
                  const tc = TYPE_COLORS[ev.type] || { bg: '#f1f5f9', color: '#475569' };
                  return (
                    <tr key={ev._id}>
                      <td className="coach-insights__ev-time" title={new Date(ev.createdAt).toLocaleString()}>
                        {timeAgo(ev.createdAt)}
                      </td>
                      <td>
                        <span className="coach-insights__badge" style={{ background: tc.bg, color: tc.color }}>
                          {TYPE_LABELS[ev.type] || ev.type}
                        </span>
                      </td>
                      <td>
                        {ev.playerId ? (
                          <button
                            type="button"
                            className="coach-insights__player-link"
                            onClick={() => handlePlayerClick(ev.playerId)}
                          >
                            {ev.playerId.substring(0, 8)}…
                          </button>
                        ) : (
                          <span className="coach-insights__no-player">—</span>
                        )}
                      </td>
                      <td className="coach-insights__ev-detail">
                        {(() => {
                          const m = ev.metadata || {};
                          if (ev.type === 'search_executed') {
                            const f = m.filters || {};
                            const parts = [];
                            if (f.sport) parts.push(f.sport);
                            if (f.gender) parts.push(f.gender);
                            if (f.graduationYear) parts.push(f.graduationYear);
                            return parts.join(' · ') || '—';
                          }
                          if (m.source) return `from ${m.source}`;
                          if (m.context) return m.context;
                          if (m.from) return `from ${m.from}`;
                          return '—';
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachInsights;
